import React, { useCallback, useEffect, useState } from 'react';
import { SnackbarMessage } from './types';
import styles from './Snackbar.module.css';

interface SnackbarProps {
  message: SnackbarMessage | null;
  onClose: () => void;
}

export const Snackbar: React.FC<SnackbarProps> = ({ message, onClose }) => {
  const [progress, setProgress] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Match animation duration
  }, [onClose]);

  useEffect(() => {
    if (message) {
      setIsVisible(true);
      setProgress(0);

      if (message.hideDelay) {
        const startTime = Date.now();
        const interval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const newProgress = (elapsed / message.hideDelay!) * 100;

          if (newProgress >= 100) {
            clearInterval(interval);
            handleClose();
          } else {
            setProgress(newProgress);
          }
        }, 16); // ~60fps

        return () => clearInterval(interval);
      }
    } else {
      setIsVisible(false);
    }
  }, [message, handleClose]);

  if (!message) {
    return null;
  }

  return (
    <div className={`${styles.snackbar} ${isVisible ? styles.visible : ''} ${styles[message.severity]}`}>
      <div className={styles.content}>
        <span className={styles.message}>{message.message}</span>

        {message.action && (
          <button
            className={styles.actionButton}
            onClick={() => {
              message.action!.callback();
              handleClose();
            }}
          >
            {message.action.name}
          </button>
        )}
      </div>

      <button className={styles.closeButton} onClick={handleClose}>
        {message.hideDelay ? (
          <div className={styles.closeWithProgress}>
            <svg className={styles.progressCircle} viewBox="0 0 24 24">
              <circle className={styles.progressBackground} cx="12" cy="12" r="10" />
              <circle
                className={styles.progressForeground}
                cx="12"
                cy="12"
                r="10"
                style={{
                  strokeDashoffset: `${62.83 - (62.83 * progress) / 100}`,
                }}
              />
            </svg>
            <span className={styles.closeIcon}>×</span>
          </div>
        ) : (
          <span className={styles.closeIcon}>×</span>
        )}
      </button>
    </div>
  );
};
